"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getLocaleDays = exports.getLocaleDateFormat = exports.getCurrentLocale = exports.autoDateSeparator = exports.MINDATE = exports.MAXDATE = void 0;
var _dateFns = require("date-fns");
var _locale = require("date-fns/locale");
var getBrowserLanguage = function getBrowserLanguage() {
  if (typeof document === "undefined") {
    return "en";
  }
  return document.documentElement.getAttribute("lang") || navigator.language || "en";
};
var locales = {
  "en-US": _locale.enUS,
  en: _locale.enUS,
  de: _locale.de
};
var MINDATE = new Date(100, 0, 1); // 01.01.100
exports.MINDATE = MINDATE;
var MAXDATE = new Date(9999, 11, 31); // 31.12.9999
/**
 * This provides the locale object according to the lang attribute set within the <html> tag on the current site.
 * The locale object is used by some functions that date-fns provide.
 * Check: https://date-fns.org/v2.0.0-alpha.26/docs/I18n
 */
exports.MAXDATE = MAXDATE;
var getCurrentLocale = function getCurrentLocale() {
  return locales[getBrowserLanguage()] || _locale.de;
};
/**
 * This returns dateFormats that are used in the current locale. Example for de locale:
 * full: 'EEEE, do MMMM, y',
 * long: 'do MMMM, y',
 * medium: 'd MMM, y',
 * short: 'dd.MM.y'
 */
exports.getCurrentLocale = getCurrentLocale;
var getLocaleDateFormat = function getLocaleDateFormat(width) {
  return getCurrentLocale().formatLong.date({
    width: width
  });
};
/**
 * This returns dayFormats that are used in the current locale. Example for de locale:
 * narrow: ['S', 'M', 'D', 'M', 'D', 'F', 'S'],
 * short: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
 * abbreviated: ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'],
 * wide: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']
 */
exports.getLocaleDateFormat = getLocaleDateFormat;
var getLocaleDays = function getLocaleDays(width) {
  return [0, 1, 2, 3, 4, 5, 6].map(function (i) {
    return getCurrentLocale().localize.day(i, {
      width: width
    });
  });
};
/**
 * This automatically inserts seperators based on the current locale to the given value.
 * Example for de locale:
 *
 * value: 1
 *
 * return: 1
 *
 * value: 12
 *
 * return: 12.
 */
exports.getLocaleDays = getLocaleDays;
var autoDateSeparator = function autoDateSeparator(value) {
  var minDate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MINDATE;
  var maxDate = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : MAXDATE;
  /* get date-fns locale short format as short format will always be the way to input a date inside a datepicker input
     short format example in de locale: "dd.MM.y" */
  var localeFormat = getLocaleDateFormat("short");
  var possibleSeperators = ["-", "/", "."]; // please add if there are more
  var currentSeparator = possibleSeperators.find(function (sep) {
    return localeFormat.includes(sep);
  });
  /* because date-fns' locale may only have one y as year format, build a "fullFormat" with 4 ys as we'll need the length later */
  var fullFormat = localeFormat.toLowerCase().split(currentSeparator).map(function (block) {
    return block.replace(" ", "");
  }) // there are spaces within some date formats - just remove those
  .map(function (block) {
    return block.includes("y") && block.length !== 4 ? "yyyy" : block;
  }).join(currentSeparator);
  /* get with just one one letter so we know the order */
  var dmyOrder = fullFormat.split(currentSeparator).map(function (block) {
    return block[0];
  });
  /* just some short helper functions to help working with the array - self explanatory I guess */
  var before = function before(val) {
    return val.substr(0, val.length - 1);
  };
  var last = function last(val) {
    return val[val.length - 1];
  };
  /* add leading zeros until string has given length */
  var leadingZeros = function leadingZeros(val, length) {
    return val.length === length ? val : "".concat("0".repeat(length - val.length)).concat(val);
  };
  /* takes value as string, min and max numbers and length, keeps given value within min and max and returns it with leading zeros in given length */
  var valMinMax = function valMinMax(val, min, max, length) {
    return leadingZeros(Math.min(Math.max(parseInt(val, 10), min), max).toString(), length);
  };
  /* tests the first char in a day/month block and returns value */
  var checkFirstChar = function checkFirstChar(val, pass, fill) {
    return pass.test(last(val)) && val ||
    // if the last char of val passes, just continue with the new val
    fill.test(last(val)) && makeValidDate("".concat(before(val), "0").concat(last(val)).split(currentSeparator));
  }; // if the last char of val passes the fill test, fill the block with zeros and make a valid date again
  /* date formatting works also for each char sequentially
   * check first char - get whether its day or month from locale, test if value fits accordingly (0-3 for days, 0-1 for months)
   * check second char - get whether its day or month from locale, test if value fits accordingly
   * [...]
   */
  var getNewValue = function getNewValue(val) {
    var dateBlocks = val.split(currentSeparator).filter(function (block) {
      return !!block;
    }); // get non empty d m y blocks as array ([dd, mm, yyyy])
    var dmy = fullFormat[val.length - 1]; // get the current character from fullFormat (d/m/y or separator)
    var lastSeparatorIndex = "".concat(currentSeparator).concat(val).lastIndexOf(currentSeparator); // get the position of the last separator in value (also add one to the beginning, so there always is one)
    var pos = val.length - lastSeparatorIndex; // the position within the current d/m/y block - so 1 or 2 for dd/mm and 1 to 4 within yyyy
    if (possibleSeperators.includes(last(val))) {
      // user entered separator?
      if (pos === 1 || last(before(val)) === currentSeparator) {
        return before(val); // on first position of any block or if there is already a separator, revoke the input
      }

      return dmy === currentSeparator && val ||
      // if separator is allowed in this position, accept it
      makeValidDate(dateBlocks); // else fill block with leading zeros and go to next block
    }

    return dmy === "d" && pos === 1 && checkFirstChar(val, /[0-3]/, /[4-9]/) ||
    // user entered 0-3 as the first char within day block --> pass the new value through, user entered 4-9 --> fill the block with zeros and go to next block
    dmy === "m" && pos === 1 && checkFirstChar(val, /[0-1]/, /[2-9]/) ||
    // user entered 0-1 as the first char within month block --> pass the new value through, user entered 2-9 --> fill the block with zeros and go to next block
    dmy === "y" && pos !== 4 && val ||
    // just pass chars on position 1, 2, 3 of year
    (pos === 2 || pos === 4) && makeValidDate(dateBlocks); // after the last position of any block is entered, make a valid date again
  };
  /* there are two times when we can evaluate if the date is valid so far:
   * - after the first 2 blocks are entered (we take year 2000 as fallback, because all months have max length)
   * - after the whole date has been entered
   * at these times we might alter the whole date again
   * makeValidDate checks if the blocks are valid
   * afterwards the blocks will be joined again using the currentSeparator
   */
  var makeValidDate = function makeValidDate(dateBlocks) {
    var newDate = dateBlocks.map(function (curr, i) {
      return dmyOrder[i] === "d" && getMaxValidDay(dateBlocks) ||
      // if current block is day, get the maximum valid day in specified month/year
      dmyOrder[i] === "m" && valMinMax(curr, 1, 12, 2) ||
      // if current block is month, just make sure it is within 1 to 12
      curr.split("").filter(function (_char) {
        return !possibleSeperators.includes(_char);
      }).join("");
    } // just pass the year block - filter separators
    ).map(function (curr, i) {
      return "".concat(curr).concat(i !== 2 ? currentSeparator : "");
    }) // add Separator except after last block (i === 2)
    .join("");
    var parsedNewDate = newDate.length === fullFormat.length && (0, _dateFns.parse)(newDate, localeFormat, new Date());
    var formattedMinDate = (0, _dateFns.format)(minDate, localeFormat);
    var formattedMaxDate = (0, _dateFns.format)(maxDate, localeFormat);
    return parsedNewDate && (0, _dateFns.isBefore)(parsedNewDate, minDate) && formattedMinDate || parsedNewDate && (0, _dateFns.isAfter)(parsedNewDate, maxDate) && formattedMaxDate || newDate;
  };
  /* getMaxValidDay checks if the given day is a possible value in the given month and - if given - in the given year (otherwise uses year 2000 as fallback) */
  var getMaxValidDay = function getMaxValidDay(blocks) {
    var year = blocks[dmyOrder.indexOf("y")] || "2000"; // use year 2000 as fallback because it's a leap-year
    var month = blocks[dmyOrder.indexOf("m")] || "13"; // month is always +1 (dunno why)
    var day = blocks[dmyOrder.indexOf("d")];
    var maxDay = (0, _dateFns.getDaysInMonth)(new Date(parseInt(year, 10), parseInt(month, 10) - 1)); // get count of days in month (and year)
    return valMinMax(day, 1, maxDay, 2); // if input day is bigger than max, return max, else input
  };
  /* only generate a new value if the new value is still within the length of the format - otherwise just ignore the last char */
  return value.length > fullFormat.length ? before(value) :
  /* go through every char in value and generate the new value based on the return of getNewValue
   * acc will always contain the full value up until the current step
   */
  value.split("").reduce(function (acc, curr) {
    return getNewValue("".concat(acc).concat(curr)) || acc;
  }, "");
};
exports.autoDateSeparator = autoDateSeparator;